/**
 * Project Initiation Dialog
 * Multi-step wizard for creating projects with workflow selection.
 * Supports: Manual Calculation, External File Import, Live Bridge
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { X, Upload, Plug, Calculator, ChevronRight, FileUp, Wifi } from 'lucide-react'
import { useQuery, useMutation } from '@tanstack/react-query'
import api from '../services/api'
import { toast } from 'react-hot-toast'
import { useTouchedGuard } from '../hooks/useUnsavedChanges'

const WORKFLOW_TYPES = {
  MANUAL: {
    id: 'manual',
    name: 'Handmatige Berekening',
    description: 'Maak project zonder 3D model (QuickStart Wizard)',
    icon: Calculator,
    color: 'bg-blue-500'
  },
  EXTERNAL_IMPORT: {
    id: 'external_import',
    name: 'Bestand Importeren',
    description: 'Upload een SKP bestand van SketchUp of CabinetSense',
    icon: FileUp,
    color: 'bg-green-500'
  },
  LIVE_BRIDGE: {
    id: 'live_bridge',
    name: 'Live Verbinding',
    description: 'Verbind met SketchUp Plugin voor real-time synchronisatie',
    icon: Wifi,
    color: 'bg-purple-500'
  }
}

export default function ProjectInitiationDialog({ isOpen, onClose }) {
  const { t } = useTranslation()
  const guard = useTouchedGuard(isOpen)
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [selectedWorkflow, setSelectedWorkflow] = useState(null)
  const [projectName, setProjectName] = useState('')
  const [selectedKlantId, setSelectedKlantId] = useState(null)
  const [isWaitingForPlugin, setIsWaitingForPlugin] = useState(false)

  // Fetch klanten for step 2
  const { data: klantenData } = useQuery({
    queryKey: ['klanten'],
    queryFn: async () => {
      const response = await api.get('/klanten')
      return response.data.items || response.data || []
    },
    enabled: step >= 2
  })

  // Create project mutation
  const createProjectMutation = useMutation({
    mutationFn: async (projectData) => {
      const response = await api.post('/projecten', projectData)
      return response.data
    },
    onSuccess: (data) => {
      toast.success(t('dialogs.projectInitiation.dossierAangemaakt', 'Dossier succesvol aangemaakt!'))
      
      // Route based on workflow
      if (selectedWorkflow === 'manual') {
        navigate(`/quick-start?project_id=${data.id}&klant_id=${selectedKlantId}`)
      } else if (selectedWorkflow === 'external_import') {
        navigate(`/import/sketchup?project_id=${data.id}&workflow=external_import`)
      } else if (selectedWorkflow === 'live_bridge') {
        setIsWaitingForPlugin(true)
        // Show waiting state - plugin will push data
      }
      
      onClose()
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.detail || error.message || t('dialogs.projectInitiation.foutAanmaken', 'Fout bij aanmaken project')
      toast.error(errorMessage || t('errors.genericApiError', 'Er is een onverwachte fout opgetreden.'))
    }
  })

  const handleWorkflowSelect = (workflowId) => {
    setSelectedWorkflow(workflowId)
    setStep(2)
  }

  const handleNext = () => {
    if (step === 2) {
      // Validate inputs
      if (!projectName.trim()) {
        toast.error(t('dialogs.projectInitiation.dossierNaamVerplicht', 'Dossier naam is verplicht'))
        return
      }
      if (!selectedKlantId) {
        toast.error(t('dialogs.projectInitiation.selecteerKlantToast', 'Selecteer een klant'))
        return
      }

      // Create project (default to B2C)
      const projectData = {
        name: projectName.trim(),
        klant_id: selectedKlantId,
        data_source: selectedWorkflow,
        segment_type: 'B2C'  // Default to B2C (Dossier)
      }

      createProjectMutation.mutate(projectData)
    }
  }

  const handleBack = () => {
    if (step === 2) {
      setStep(1)
      setProjectName('')
      setSelectedKlantId(null)
    }
  }

  const handleClose = () => {
    setStep(1)
    setSelectedWorkflow(null)
    setProjectName('')
    setSelectedKlantId(null)
    setIsWaitingForPlugin(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) guard.closeWith(handleClose) }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Nieuw Dossier Aanmaken
          </h2>
          <button
            onClick={() => guard.closeWith(handleClose)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" onChange={guard.onContentChange}>
          {step === 1 && (
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Hoe wil je dit dossier starten?
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.values(WORKFLOW_TYPES).map((workflow) => {
                  const Icon = workflow.icon
                  return (
                    <button
                      key={workflow.id}
                      onClick={() => handleWorkflowSelect(workflow.id)}
                      className="p-6 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-lg transition-all text-left group"
                    >
                      <div className={`${workflow.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {workflow.name}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {workflow.description}
                      </p>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`${WORKFLOW_TYPES[selectedWorkflow.toUpperCase()]?.color || 'bg-gray-500'} w-8 h-8 rounded-lg flex items-center justify-center`}>
                    {(() => {
                      const Icon = WORKFLOW_TYPES[selectedWorkflow.toUpperCase()]?.icon || Calculator
                      return <Icon className="w-4 h-4 text-white" />
                    })()}
                  </div>
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {WORKFLOW_TYPES[selectedWorkflow.toUpperCase()]?.name}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Dossier Informatie
                </h3>
              </div>

              <div className="space-y-4">
                {/* Project Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Project Naam *
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Bijv. Keuken Renovatie 2026"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Klant Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Klant *
                  </label>
                  <select
                    value={selectedKlantId || ''}
                    onChange={(e) => setSelectedKlantId(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Selecteer een klant...</option>
                    {klantenData?.map((klant) => (
                      <option key={klant.id} value={klant.id}>
                        {klant.naam || `${klant.voornaam || ''} ${klant.achternaam || ''}`.trim() || `Klant #${klant.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Workflow-specific info */}
                {selectedWorkflow === 'live_bridge' && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      <strong>Volgende stap:</strong> Open SketchUp en gebruik de Orion Plugin om dit project te verbinden.
                      Het project wordt automatisch aangemaakt wanneer de plugin data pusht.
                    </p>
                  </div>
                )}

                {selectedWorkflow === 'external_import' && (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="text-sm text-green-800 dark:text-green-300">
                      <strong>Volgende stap:</strong> Na het aanmaken van het project kun je een SKP bestand uploaden en materialen mappen.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {isWaitingForPlugin && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Wachten op SketchUp Plugin...
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Open SketchUp en gebruik de Orion Plugin om data te pushen.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isWaitingForPlugin && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleBack}
              disabled={step === 1}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Terug
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => guard.closeWith(handleClose)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                Annuleren
              </button>
              {step === 2 && (
                <button
                  onClick={handleNext}
                  disabled={createProjectMutation.isPending || !projectName.trim() || !selectedKlantId}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {createProjectMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Aanmaken...
                    </>
                  ) : (
                    <>
                      Volgende
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
